import { CalendarHeart, Baby, AlertCircle, Sprout } from "lucide-react";
import { PHASES } from "@/lib/lunelle-data";
import { todaySummary, lateBy, pregnancyInfo, infoForDate } from "@/lib/cycle";

const Label = ({ icon: Icon, children }) => (
  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#E8B99A]"><Icon className="h-4 w-4" /> {children}</p>
);

const PregnancyView = ({ cycle }) => {
  const p = pregnancyInfo(cycle);
  if (!p) return <p className="font-serif text-2xl" data-testid="pregnancy-empty">Enter the first day of your last period below to see your weeks and due date.</p>;
  return (
    <>
      <Label icon={Baby}>Pregnancy</Label>
      <p className="mt-2 font-serif text-4xl" data-testid="pregnancy-weeks">Week {p.weeks} + {p.extraDays} {p.extraDays === 1 ? "day" : "days"}</p>
      <p className="mt-1 text-sm text-[#d9c7bd]" data-testid="pregnancy-due">{p.trimester} trimester · Estimated due date {p.due}{p.daysToGo > 0 ? ` · ${p.daysToGo} days to go` : ""}</p>
    </>
  );
};

const CycleView = ({ cycle }) => {
  const t = todaySummary(cycle);
  if (!t) return <p className="font-serif text-2xl" data-testid="cycle-empty">Tap a day on the calendar, then "My period started" to begin.</p>;
  const phase = PHASES.find((p) => p.id === t.phase);
  const late = lateBy(cycle);
  const fertile = infoForDate(cycle, new Date())?.fertile;
  return (
    <>
      <Label icon={CalendarHeart}>Today</Label>
      <p className="mt-2 font-serif text-4xl" data-testid="cycle-today-phase">Day {t.day} · {phase.name}</p>
      <p className="mt-1 text-sm text-[#d9c7bd]" data-testid="cycle-next-period">Next period in about {t.daysToNext} {t.daysToNext === 1 ? "day" : "days"} ({t.nextPeriod}) · {t.len}-day cycle</p>
      {fertile && !late && (
        <p className="mt-4 flex items-start gap-2 rounded-xl bg-white/10 p-3 text-sm text-[#FBF5EE]" data-testid="cycle-fertile-note">
          <Sprout className="mt-0.5 h-4 w-4 shrink-0 text-[#A8D5BA]" /> You're in your fertile window, so your chance of pregnancy is highest these days.
        </p>
      )}
      {late > 0 && (
        <p className="mt-4 flex items-start gap-2 rounded-xl bg-white/10 p-3 text-sm text-[#FBF5EE]" data-testid="cycle-late-note">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#E8B99A]" /> Your period is about {late} days late. Stress and travel can shift cycles, but if there's a chance you're pregnant, consider taking a test.
        </p>
      )}
    </>
  );
};

export const TodayCard = ({ cycle }) => (
  <div className="mt-8 rounded-[1.5rem] bg-[#2C221E] p-6 text-[#FBF5EE]" data-testid="cycle-today-card">
    {cycle.pregnant ? <PregnancyView cycle={cycle} /> : <CycleView cycle={cycle} />}
  </div>
);
